import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, FileText, Loader2, Mic, MicOff, Crop, ClipboardPaste } from 'lucide-react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import useVoiceRecognition from '../hooks/useVoiceRecognition';
import useMobileModalMode from '../hooks/useMobileModalMode';
import { parseCollectionVoice } from '../utils/voiceHelper';
import api from '../utils/api';
import { getLocalDateInputValue, toDateInputValue } from '../utils/dateUtils';
import toast from 'react-hot-toast';

const AddCollectionModal = ({ isOpen, onClose, onRefresh, editData = null, existingCollections = [] }) => {
    const [formData, setFormData] = useState({
        seller: '',
        buyer: '',
        institution: '',
        amount: '',
        currency: 'USD',
        notes: '',
        transferNo: '',
        imageUrl: '',
        createdAt: getLocalDateInputValue()
    });

    // Otomatik Doldurma İçin Eşsiz (Benzersiz) Listeler
    const uniqueSellers = Array.from(new Set(existingCollections.map(c => c.seller))).filter(Boolean);
    const uniqueBuyers = Array.from(new Set(existingCollections.map(c => c.buyer))).filter(Boolean);
    const uniqueInstitutions = Array.from(new Set(existingCollections.map(c => c.institution))).filter(Boolean);

    React.useEffect(() => {
        setImgSrc('');
        setCrop(undefined);
        setCompletedCrop(null);
        if (editData && isOpen) {
            setFormData({
                seller: editData.seller || '',
                buyer: editData.buyer || '',
                institution: editData.institution || '',
                amount: editData.amount || '',
                currency: editData.currency || 'USD',
                notes: editData.notes || '',
                transferNo: editData.transferNo || '',
                imageUrl: editData.imageUrl || '',
                createdAt: toDateInputValue(editData.createdAt)
            });
        } else if (!editData && isOpen) {
            setFormData({
                seller: '', buyer: '', institution: '', amount: '', currency: 'USD', notes: '', transferNo: '', imageUrl: '', createdAt: getLocalDateInputValue()
            });
        }
    }, [editData, isOpen]);

    const [isOcrLoading, setIsOcrLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Image Cropping States
    const [imgSrc, setImgSrc] = useState('');
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const imgRef = useRef(null);

    const { isListening, supported, text, startListening, stopListening, setText } = useVoiceRecognition();

    useMobileModalMode(isOpen);

    React.useEffect(() => {
        if (text && !isListening) {
            const sellersForVoice = uniqueSellers.map(s => ({ name: s }));
            const parsed = parseCollectionVoice(text, sellersForVoice);

            setFormData(prev => ({
                ...prev,
                seller: parsed.seller || prev.seller,
                institution: parsed.institution || prev.institution,
                amount: parsed.amount || prev.amount,
                currency: parsed.currency || prev.currency,
                notes: prev.notes ? `${prev.notes}\nSes: ${text}` : `Ses: ${text}`
            }));

            toast.success(`Ses algılandı ve forma aktarıldı.`);
            setText('');
        }
    }, [text, isListening, uniqueSellers, setText]);

    const toTitleCase = (str) => {
        if (!str) return '';
        return str.toLocaleLowerCase('tr-TR').split(' ').map(word =>
            word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1)
        ).join(' ');
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        // Eğer input bir text veya textarea ise Title Case yap
        const newValue = (type === 'text' || e.target.tagName.toLowerCase() === 'textarea') && name !== 'transferNo' // Transfer no genelde büyük veya sayıdır, dokunma
            ? toTitleCase(value)
            : value;

        setFormData({ ...formData, [name]: newValue });
    };

    const prepareReceiptImage = useCallback((file) => {
        if (!file || !file.type?.startsWith('image/')) {
            toast.error('Panoda veya seçilen dosyada geçerli bir görsel bulunamadı.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Görsel en fazla 10 MB olabilir.');
            return;
        }

        setCrop(undefined);
        setCompletedCrop(null);
        const reader = new FileReader();
        reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
        reader.addEventListener('error', () => toast.error('Görsel okunamadı.'));
        reader.readAsDataURL(file);
    }, []);

    // FOTOĞRAF SEÇİLDİĞİNDE (KIRPMA İÇİN HAZIRLA)
    const handleFileChange = (e) => {
        prepareReceiptImage(e.target.files?.[0]);
        e.target.value = '';
    };

    React.useEffect(() => {
        if (!isOpen) return undefined;

        const handlePaste = (event) => {
            const imageItem = Array.from(event.clipboardData?.items || [])
                .find((item) => item.kind === 'file' && item.type.startsWith('image/'));
            if (!imageItem) return;

            event.preventDefault();
            prepareReceiptImage(imageItem.getAsFile());
            toast.success('Ekran görüntüsü yapıştırıldı. Kırpıp yükleyebilirsiniz.');
        };

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [isOpen, prepareReceiptImage]);

    if (!isOpen) return null;

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const _crop = centerCrop(
            makeAspectCrop({ unit: '%', width: 90 }, 1, width, height),
            width,
            height
        );
        setCrop(_crop);
    }

    // KIRPMA İŞLEMİNİ ONAYLA VE GÖNDER
    const handleCropComplete = async () => {
        if (!completedCrop || !imgRef.current) {
            return;
        }

        const image = imgRef.current;
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const pixelRatio = window.devicePixelRatio;

        canvas.width = Math.floor(completedCrop.width * scaleX * pixelRatio);
        canvas.height = Math.floor(completedCrop.height * scaleY * pixelRatio);

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingQuality = 'high';

        const cropX = completedCrop.x * scaleX;
        const cropY = completedCrop.y * scaleY;
        const cropWidth = completedCrop.width * scaleX;
        const cropHeight = completedCrop.height * scaleY;

        ctx.drawImage(
            image,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            cropWidth,
            cropHeight
        );

        // Convert canvas to Blob
        canvas.toBlob(async (blob) => {
            if (!blob) return;
            const croppedFile = new File([blob], 'cropped_receipt.jpg', { type: 'image/jpeg' });

            setIsOcrLoading(true);
            setImgSrc(''); // Hide crop tool

            const imgData = new FormData();
            imgData.append('receiptImage', croppedFile);

            try {
                // Backend'in OCR Analiz servisine yolla
                const res = await api.post('/collections/upload', imgData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                // Resmin URL'sini doğrudan formData'ya göm
                const uploadedUrl = res.data.imageUrl;

                // Eğer Transfer No bulabildiyse Form'a otomatik yaz
                if (res.data.possibleTransferNo && res.data.possibleTransferNo !== 'Tanımlanamadı, lütfen manuel girin.') {
                    setFormData(prev => ({ ...prev, transferNo: res.data.possibleTransferNo, imageUrl: uploadedUrl }));
                    toast.success(`Dekonttan Transfer No Okundu: ${res.data.possibleTransferNo}`);
                } else {
                    setFormData(prev => ({ ...prev, imageUrl: uploadedUrl }));
                    toast('Fotoğraf yüklendi. Transfer no okunamadı — manuel girin.', { icon: '📋' });
                }

            } catch (error) {
                console.error("OCR Hatası", error);
                toast.error('Görüntü işlenirken hata oluştu.');
            } finally {
                setIsOcrLoading(false);
            }
        }, 'image/jpeg');
    };

    // FORMU KAYDET
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // 1. Önce tahsilat kaydını oluştur
            const submitData = { ...formData, amount: parseFloat(formData.amount || 0) };

            // EĞER ID VARSA GÜNCELLE, YOKSA EKLE
            if (editData && editData.id) {
                // Admin bir kaydı düzenlediğinde tutar tek doğru değere döner:
                // kalan bakiye yeni tutara eşitlenir, eski kısmi tahsilat ("Asıl: X") izi silinir.
                submitData.remainingAmount = parseFloat(formData.amount || 0);
                await api.put(`/collections/${editData.id}`, submitData);
            } else {
                await api.post('/collections', submitData);
            }

            // 2. Eğer fotoğraf seçiliyse (fakat önceden OCR ile atmadıysak veya ek link için)
            // (Şimdilik OCR anında fotoğrafı sunucuya /uploads'a attığımız için bu kısmı atlıyoruz, ileri düzeyde imageUrl update eklenebilir)

            onRefresh({ resetToFirstPage: !editData }); // Ana sayfadaki listeyi yenile
            onClose(); // Modalı kapat
        } catch (error) {
            console.error('Kayıt eklenemedi', error);
            toast.error('Kayıt işlemi başarısız. Yetkinizi kontrol edin.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/80 backdrop-blur-sm sm:items-center sm:p-4">
            <div className="bg-dark-card border border-slate-700 w-full max-w-2xl text-white rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-0.5rem)] sm:max-h-[90vh] sm:rounded-2xl">

                {/* MODAL HEADER */}
                <div className="flex justify-between items-center p-5 sm:p-6 border-b border-slate-700/50 bg-slate-800/30">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <h2 className="text-xl font-bold">{editData ? "Kayıt Güncelleme" : "Yeni Tahsilat Kaydı"}</h2>
                        {supported && !editData && (
                            <button
                                type="button"
                                onClick={isListening ? stopListening : startListening}
                                className={`flex flex-1 sm:flex-none items-center justify-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${isListening
                                    ? 'bg-red-500/20 text-red-400 animate-pulse border border-red-500/50 shadow-lg shadow-red-500/20'
                                    : 'bg-primary-500/20 text-primary-400 hover:bg-primary-500/30 border border-primary-500/30'
                                    }`}
                                title="Sesle Doldur"
                            >
                                {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                                {isListening ? 'Dinleniyor...' : 'Sesle Doldur (Akıllı Kayıt)'}
                            </button>
                        )}
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-full transition-colors shrink-0">
                        <X size={20} />
                    </button>
                </div>

                {/* MODAL BODY (SCROLL KISMI) */}
                <div className="p-5 sm:p-6 overflow-y-auto custom-scrollbar">
                    <form id="addForm" onSubmit={handleSubmit} className="space-y-5">

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Satıcı</label>
                                <input type="text" name="seller" list="seller-list" value={formData.seller} onChange={handleChange} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500" placeholder="Örn: Abc Firması" />
                                <datalist id="seller-list">
                                    {uniqueSellers.map((s, i) => <option key={`sell-${i}`} value={s} />)}
                                </datalist>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Alıcı</label>
                                <input type="text" name="buyer" list="buyer-list" value={formData.buyer} onChange={handleChange} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500" placeholder="Örn: Xyz Ltd." />
                                <datalist id="buyer-list">
                                    {uniqueBuyers.map((b, i) => <option key={`buy-${i}`} value={b} />)}
                                </datalist>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Kurum</label>
                                <input type="text" name="institution" list="institution-list" value={formData.institution} onChange={handleChange} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500" placeholder="Örn: İş Bankası" />
                                <datalist id="institution-list">
                                    {uniqueInstitutions.map((inst, i) => <option key={`inst-${i}`} value={inst} />)}
                                </datalist>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Tutar</label>
                                <input type="number" step="0.01" name="amount" value={formData.amount} onChange={handleChange} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500" placeholder="0.00" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Döviz</label>
                                <select name="currency" value={formData.currency} onChange={handleChange} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500">
                                    <option value="USD">USD ($)</option>
                                    <option value="EUR">EUR (€)</option>
                                    <option value="GBP">GBP (£)</option>
                                    <option value="TRY">TRY (₺)</option>
                                </select>
                            </div>
                        </div>

                        {/* Tarih ve Transfer No */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Kayıt Tarihi</label>
                                <input
                                    type="date"
                                    name="createdAt"
                                    value={formData.createdAt}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500 text-white cursor-pointer"
                                />
                                <p className="text-xs text-slate-500 mt-1">Varsayılan: Bugünün tarihi. Gerekirse değiştirin.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Transfer / Dekont No</label>
                                <input type="text" name="transferNo" value={formData.transferNo} onChange={handleChange} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500" placeholder="Örn: TR1234567" />
                            </div>
                        </div>

                        {/* OCR FOTOĞRAF YÜKLEME ALANI VE KIRPMA ARACI */}
                        {!imgSrc && (
                            <div className="mt-6 border-2 border-dashed border-slate-600 rounded-xl p-6 text-center hover:border-primary-500/50 transition-colors bg-slate-800/20 relative">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                    disabled={isOcrLoading}
                                />
                                <div className="flex flex-col items-center justify-center space-y-2 pointer-events-none">
                                    {isOcrLoading ? (
                                        <>
                                            <Loader2 className="animate-spin text-primary-500" size={32} />
                                            <p className="text-sm font-medium text-primary-400">Görüntü İşleniyor...</p>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex items-center gap-2">
                                                <div className="p-3 bg-slate-800 rounded-full text-primary-400"><Upload size={24} /></div>
                                                <div className="p-3 bg-slate-800 rounded-full text-cyan-400"><ClipboardPaste size={24} /></div>
                                            </div>
                                            <p className="text-sm font-medium text-slate-300">Dekont / Makbuz Fotoğrafı Yükle</p>
                                            <p className="text-xs text-slate-500">Dosya seçin veya ekran görüntüsünü Ctrl+V ile yapıştırın. Önce kırpma ekranı açılır.</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* KIRPMA EKRANI */}
                        {imgSrc && (
                            <div className="mt-6 border border-slate-700 bg-slate-800/30 p-4 rounded-xl flex flex-col items-center">
                                <p className="text-sm text-slate-300 mb-3 font-medium flex items-center gap-2">
                                    <Crop size={18} className="text-primary-400" /> Görüntünün ilgili kısmını seçin
                                </p>
                                <ReactCrop
                                    crop={crop}
                                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                                    onComplete={(c) => setCompletedCrop(c)}
                                    className="max-h-[300px] mb-4 overflow-hidden rounded-lg flex justify-center"
                                >
                                    <img
                                        ref={imgRef}
                                        src={imgSrc}
                                        alt="Kırpılacak fotoğraf"
                                        onLoad={onImageLoad}
                                        style={{ maxHeight: '300px', width: 'auto' }}
                                    />
                                </ReactCrop>
                                <div className="flex gap-3 w-full">
                                    <button
                                        type="button"
                                        onClick={() => setImgSrc('')}
                                        className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        İptal
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCropComplete}
                                        disabled={!completedCrop?.width || !completedCrop?.height}
                                        className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg text-sm font-bold transition-all disabled:opacity-50"
                                    >
                                        Seçili Alanı Kes ve Yükle
                                    </button>
                                </div>
                            </div>
                        )}
                        {formData.imageUrl && !isOcrLoading && (
                            <div className="text-sm text-green-400 flex items-center justify-between bg-green-500/10 border border-green-500/20 p-3 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <FileText size={18} />
                                    <span>Fotoğraf başarıyla eklendi <span className="hidden sm:inline">ve sisteme kaydedilecek</span>.</span>
                                </div>
                                <a href={process.env.NODE_ENV === 'production' ? formData.imageUrl : `http://localhost:5000${formData.imageUrl}`} target="_blank" rel="noopener noreferrer" className="text-green-300 underline hover:text-white px-2 cursor-pointer z-10" onClick={(e) => e.stopPropagation()}>Gör</a>
                                <button type="button" onClick={() => { setFormData({ ...formData, imageUrl: '' }); }} className="text-slate-400 hover:text-red-400 px-2 cursor-pointer z-10">Kaldır</button>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-300 mb-1">Transfer Numarası (Referans)</label>
                                <input type="text" name="transferNo" value={formData.transferNo} onChange={handleChange} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-primary-400 font-mono focus:outline-none focus:border-primary-500" placeholder="Fotoğraftan okunabilir veya manuel ekleyin" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-300 mb-1">Notlar / Açıklama</label>
                                <textarea name="notes" rows="3" value={formData.notes} onChange={handleChange} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500" placeholder="Limite takılma sebebi, özel notlar vb."></textarea>
                            </div>
                        </div>
                    </form>
                </div>

                {/* MODAL FOOTER */}
                <div className="sticky bottom-0 z-10 p-4 sm:p-6 border-t border-slate-700/50 bg-slate-800/95 flex justify-end gap-3 pb-safe-offset">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg font-medium text-slate-300 hover:bg-slate-700 transition-colors">
                        Vazgeç
                    </button>
                    <button
                        type="submit"
                        form="addForm"
                        disabled={isSubmitting || isOcrLoading}
                        className="px-6 py-2.5 rounded-lg font-bold text-white bg-primary-600 hover:bg-primary-500 shadow-lg shadow-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AddCollectionModal;
